/**
 * LLM-9E — Ask Your Coach and coaching conversation session API client.
 * Promotion uses existing conversation/session endpoints only.
 */
import { http } from "@/lib/api";
import { normalizeAskCoachResponse } from "@/lib/memorySuggestionGuard";
import { normalizeCoachingMemoryManagementResponse } from "@/lib/coachingMemoryManagementGuard";

const ASK_ROOT = "/ai-coach";
const SESSION_ROOT = "/coaching-router/conversation/sessions";
const MEMORY_MANAGEMENT_ROOT = "/coaching-memory/management";
const SUGGESTION_DISMISS_ROOT = "/coaching-memory/suggestions/dismiss";

const normaliseError = (err) => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return { code: "aborted", message: "Request cancelled" };
  }
  if (err?.code === "ERR_BACKEND_URL_MISSING") {
    return {
      code: "config",
      message:
        "Coach is not configured. Set REACT_APP_BACKEND_URL and reload.",
    };
  }
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return {
      code: "network",
      message: "Cannot connect to the Endurava server.",
    };
  }
  const status = err?.response?.status;
  if (status === 401) {
    return {
      code: "unauthenticated",
      status,
      message: "Your session has expired. Please sign in again.",
    };
  }
  if (status === 403) {
    return {
      code: "forbidden",
      status,
      message: "You don't have access to this coaching resource.",
    };
  }
  if (status === 404) {
    return {
      code: "not_found",
      status,
      message: "This coaching resource is not available.",
    };
  }
  if (status === 422) {
    const detail = err?.response?.data?.detail;
    return {
      code: "invalid_request",
      status,
      message:
        (typeof detail === "string" && detail)
          || detail?.message
          || "Invalid request — please refresh and try again.",
    };
  }
  if (status === 429) {
    return {
      code: "rate_limited",
      status,
      message: "Coach is busy. Please try again in a moment.",
    };
  }
  if (status >= 500) {
    return {
      code: "server_error",
      status,
      message: "Coach is temporarily unavailable.",
    };
  }
  const detailMessage = err?.response?.data?.detail?.message;
  return {
    code: "unknown",
    status,
    message: typeof detailMessage === "string" && detailMessage
      ? detailMessage
      : "Coach is temporarily unavailable.",
  };
};

const wrapError = (err) => {
  const wrapped = new Error(normaliseError(err).message);
  wrapped.aiCoachError = normaliseError(err);
  wrapped.status = err?.response?.status;
  throw wrapped;
};

export const askYourCoach = async ({
  message,
  detailLevel = "STANDARD",
  conversationId,
  clientMessageId,
  expectedConversationRevision,
}) => {
  try {
    const payload = { message, detail_level: detailLevel };
    if (conversationId) payload.conversation_id = conversationId;
    if (clientMessageId) payload.client_message_id = clientMessageId;
    if (
      typeof expectedConversationRevision === "number"
      && Number.isInteger(expectedConversationRevision)
      && expectedConversationRevision >= 1
    ) {
      payload.expected_conversation_revision = expectedConversationRevision;
    }
    const { data } = await http.post(`${ASK_ROOT}/ask`, payload);
    return {
      raw: data,
      ...normalizeAskCoachResponse(data),
    };
  } catch (err) {
    wrapError(err);
  }
  return null;
};

/**
 * Ask the AI Coach a question scoped to a specific activity. The backend
 * fetches the authoritative activity record, verifies ownership, and
 * grounds the answer in the recorded metrics before calling the shared
 * ask_your_coach service. The browser never sends activity metrics; only
 * the activity id and the user's message travel over the wire.
 */
export const askYourCoachAboutActivity = async ({
  activityId,
  message,
  detailLevel = "STANDARD",
  conversationId,
  clientMessageId,
  expectedConversationRevision,
  comparisonActivityIds,
}) => {
  if (!activityId || typeof activityId !== "string") {
    const err = new Error("activityId is required");
    err.aiCoachError = { code: "invalid_request", message: err.message };
    throw err;
  }
  try {
    const payload = { message, detail_level: detailLevel };
    if (conversationId) payload.conversation_id = conversationId;
    if (clientMessageId) payload.client_message_id = clientMessageId;
    if (
      typeof expectedConversationRevision === "number"
      && Number.isInteger(expectedConversationRevision)
      && expectedConversationRevision >= 1
    ) {
      payload.expected_conversation_revision = expectedConversationRevision;
    }
    if (Array.isArray(comparisonActivityIds) && comparisonActivityIds.length) {
      payload.comparison_activity_ids = comparisonActivityIds
        .filter((value) => typeof value === "string" && value)
        .slice(0, 6);
    }
    const { data } = await http.post(
      `${ASK_ROOT}/activities/${encodeURIComponent(activityId)}/ask`,
      payload,
    );
    return {
      raw: data,
      ...normalizeAskCoachResponse(data),
    };
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const createCoachingSession = async () => {
  try {
    const { data } = await http.post(SESSION_ROOT, {});
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const sendCoachingSessionMessage = async (
  conversationId,
  payload,
) => {
  try {
    const { data } = await http.post(
      `${SESSION_ROOT}/${conversationId}/messages`,
      payload,
    );
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const fetchCoachingMemoryManagement = async () => {
  try {
    const { data } = await http.get(MEMORY_MANAGEMENT_ROOT);
    return normalizeCoachingMemoryManagementResponse(data);
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export const dismissCoachingMemorySuggestion = async ({ feedbackRef }) => {
  if (typeof feedbackRef !== "string" || feedbackRef.trim().length === 0) {
    return null;
  }
  try {
    const { data } = await http.post(SUGGESTION_DISMISS_ROOT, {
      feedback_ref: feedbackRef,
    });
    return data;
  } catch (err) {
    wrapError(err);
  }
  return null;
};

export { normaliseError as normaliseAiCoachError };
